import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getMajorVersion } from 'helpers';
import { useSystemInfo } from './useSystemInfo';
import { getKits } from 'store/shared';
import { KitStatusValues } from '@repo/types';

export const usePreviousKitMajorVersion = () => {
  const { latestSciVersion } = useSystemInfo();
  const kits = useSelector(getKits);

  const validMajorVersions: number[] = useMemo(() => {
    if (!kits?.length) return [];
    // Extract major versions, filtering out invalid or NaN values
    return kits
      .filter(kit => kit?.status !== KitStatusValues.VOID)
      .map(kit => getMajorVersion(kit?.sci_version ?? '') ?? 0)
      .filter(version => !isNaN(version));
  }, [kits, latestSciVersion]);

  const oldestMajorVersion = useMemo(() => {
    // Return null if no valid versions exist
    if (!validMajorVersions.length) return null;
    // Get the minimum version from valid versions
    return Math.min(...validMajorVersions);
  }, [validMajorVersions, latestSciVersion]);

  const hasMultipleVersions = useMemo(
    () => new Set(validMajorVersions).size > 1,
    [validMajorVersions, latestSciVersion],
  );

  const hasOutdatedKitVersion = useMemo(
    () =>
      oldestMajorVersion !== null &&
      oldestMajorVersion != getMajorVersion(latestSciVersion ?? ''),
    [oldestMajorVersion, latestSciVersion],
  );

  return {
    oldestVersion: oldestMajorVersion,
    hasOutdatedKitVersion,
    validMajorVersions,
    hasMultipleVersions,
  };
};
